// ============================================================================
// REPORT SERVICE — Day Book, Ledger statement, Open Bills (ADR-0017).
// Entity + BU scoped via headers. Day Book and Open Bills return paginated
// envelopes with rollup totals; Ledger Statement returns its own opening/
// closing envelope shape (not paginated).
// ============================================================================

import { get } from '../client/axiosClient';
import { reportEndpoints } from '../endpoints/accounts.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  ID,
  DayBookResultDto,
  LedgerStatementDto,
  TrialBalanceQueryParams,
  TrialBalanceResultDto,
  VoucherViewDto,
  OpenBillsResultDto,
  AgeingBucket,
  DebtorsAgeingQueryParams,
  ArOutstandingQueryParams,
  ArOutstandingResultDto,
  ItemSalesQueryParams,
  ItemSalesResultDto,
  ItemSalesDetailRowDto,
  TaxSummaryQueryParams,
  TaxSummaryResultDto,
  DebtorsAgeingSummaryResultDto,
  DebtorsAgeingDetailResultDto,
} from '../types';

class ReportService {
  async getDayBook(fromDate: string, toDate: string, page = 1, pageSize = 50): Promise<DayBookResultDto> {
    const url = buildEndpointWithQuery(reportEndpoints.dayBook(), { fromDate, toDate, page, pageSize });
    const response = await get<ApiResponse<DayBookResultDto>>(url);
    return response.data.data ?? emptyDayBook(page, pageSize);
  }

  async getLedger(ledgerId: ID, fromDate: string, toDate: string): Promise<LedgerStatementDto> {
    const url = buildEndpointWithQuery(reportEndpoints.ledger(), { ledgerId, fromDate, toDate });
    const response = await get<ApiResponse<LedgerStatementDto>>(url);
    return response.data.data;
  }

  async getOpenBills(ledgerId: ID, includeClosed?: boolean, page = 1, pageSize = 50, deductionsOnly?: boolean): Promise<OpenBillsResultDto> {
    const url = buildEndpointWithQuery(reportEndpoints.openBills(), { ledgerId, includeClosed, deductionsOnly, page, pageSize });
    const response = await get<ApiResponse<OpenBillsResultDto>>(url);
    return response.data.data ?? emptyOpenBills(page, pageSize);
  }

  // --- Excel exports (permission-gated at the controller layer) ------------
  // Each export returns an .xlsx blob; downloadBlob triggers the browser save.

  async exportDayBook(fromDate: string, toDate: string): Promise<void> {
    const url = buildEndpointWithQuery(reportEndpoints.dayBookExport(), { fromDate, toDate });
    const response = await get<Blob>(url, { responseType: 'blob' });
    downloadBlob(response, `DayBook_${fromDate}_to_${toDate}.xlsx`);
  }

  async exportLedger(ledgerId: ID, fromDate: string, toDate: string): Promise<void> {
    const url = buildEndpointWithQuery(reportEndpoints.ledgerExport(), { ledgerId, fromDate, toDate });
    const response = await get<Blob>(url, { responseType: 'blob' });
    downloadBlob(response, `LedgerStatement_${fromDate}_to_${toDate}.xlsx`);
  }

  async exportOpenBills(ledgerId: ID, includeClosed?: boolean, deductionsOnly?: boolean): Promise<void> {
    const url = buildEndpointWithQuery(reportEndpoints.openBillsExport(), { ledgerId, includeClosed, deductionsOnly });
    const response = await get<Blob>(url, { responseType: 'blob' });
    downloadBlob(response, `OpenBills_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  // --- Debtors Ageing (Wave E) -------------------------------------------
  // SUMMARY (default) and DETAIL modes share a single endpoint; the ViewMode
  // in the query params picks the response shape. `buckets` (JS array) is
  // serialised to `bucketsJson` for the wire since the .NET model binder does
  // not parse complex objects from query strings reliably.

  async getDebtorsAgeingSummary(
    params: DebtorsAgeingQueryParams,
    buckets?: AgeingBucket[],
  ): Promise<DebtorsAgeingSummaryResultDto> {
    const url = appendScopeLists(buildEndpointWithQuery(
      reportEndpoints.debtorsAgeing(),
      withBuckets({ ...params, viewMode: 'SUMMARY' }, buckets),
    ), params);
    const response = await get<ApiResponse<DebtorsAgeingSummaryResultDto>>(url);
    return response.data.data ?? emptyDebtorsAgeingSummary(params.page ?? 1, params.pageSize ?? 50);
  }

  async getDebtorsAgeingDetail(
    params: DebtorsAgeingQueryParams,
    buckets?: AgeingBucket[],
  ): Promise<DebtorsAgeingDetailResultDto> {
    const url = appendScopeLists(buildEndpointWithQuery(
      reportEndpoints.debtorsAgeing(),
      withBuckets({ ...params, viewMode: 'DETAIL' }, buckets),
    ), params);
    const response = await get<ApiResponse<DebtorsAgeingDetailResultDto>>(url);
    return response.data.data ?? emptyDebtorsAgeingDetail(params.page ?? 1, params.pageSize ?? 50);
  }

  // --- AR Outstanding ----------------------------------------------------
  // Manager report: what each customer owes as at a date, straight from the
  // accounting records (ties to the AR control account). GROUPED rolls up by
  // business house; DETAILED lists customer ledgers and is paginated.

  async getArOutstanding(params: ArOutstandingQueryParams): Promise<ArOutstandingResultDto> {
    const { entityIds, businessUnitIds, ...rest } = params;
    const url = appendArScopeLists(
      buildEndpointWithQuery(reportEndpoints.arOutstanding(), rest as Record<string, unknown>),
      params,
    );
    const response = await get<ApiResponse<ArOutstandingResultDto>>(url);
    return response.data.data ?? {
      asOnDate: params.asOnDate ?? new Date().toISOString().slice(0, 10),
      viewMode: params.viewMode ?? 'GROUPED',
      rows: [], grandTotal: 0, customerCount: 0,
      baseCurrencyCode: null, mixedBaseCurrency: false,
      page: params.page ?? 1, pageSize: params.pageSize ?? 25, totalCount: 0,
    };
  }

  // --- Item-wise Sales Performance ---------------------------------------
  async getItemSales(params: ItemSalesQueryParams): Promise<ItemSalesResultDto> {
    const { entityIds, businessUnitIds, ...rest } = params;
    const url = appendArScopeLists(
      buildEndpointWithQuery(reportEndpoints.itemSales(), rest as Record<string, unknown>),
      { entityIds, businessUnitIds } as ArOutstandingQueryParams,
    );
    const response = await get<ApiResponse<ItemSalesResultDto>>(url);
    return response.data.data ?? {
      fromDate: params.fromDate ?? '', toDate: params.toDate ?? '', rows: [],
      totalSales: 0, totalCreditNotes: 0, netSales: 0, discountTotal: 0, netSalesAfterDiscount: 0,
      baseCurrencyCode: null, mixedBaseCurrency: false,
    };
  }

  async getItemSalesDetail(
    serviceId: string, params: ItemSalesQueryParams,
  ): Promise<ItemSalesDetailRowDto[]> {
    const { entityIds, businessUnitIds, fromDate, toDate } = params;
    const url = appendArScopeLists(
      buildEndpointWithQuery(reportEndpoints.itemSalesDetail(), { serviceId, fromDate, toDate }),
      { entityIds, businessUnitIds } as ArOutstandingQueryParams,
    );
    const response = await get<ApiResponse<ItemSalesDetailRowDto[]>>(url);
    return Array.from(response.data.data ?? []);
  }

  async exportItemSales(params: ItemSalesQueryParams): Promise<void> {
    const { entityIds, businessUnitIds, ...rest } = params;
    const url = appendArScopeLists(
      buildEndpointWithQuery(reportEndpoints.itemSalesExport(), rest as Record<string, unknown>),
      { entityIds, businessUnitIds } as ArOutstandingQueryParams,
    );
    const response = await get<Blob>(url, { responseType: 'blob' });
    downloadBlob(response, `ItemSales_${params.fromDate ?? ''}_${params.toDate ?? ''}.xlsx`);
  }

  // --- Tax Summary by Classification (HSN-wise summary) --------------------
  async getTaxSummary(params: TaxSummaryQueryParams): Promise<TaxSummaryResultDto> {
    const { entityIds, businessUnitIds, ...rest } = params;
    const url = appendArScopeLists(
      buildEndpointWithQuery(reportEndpoints.taxSummary(), rest as Record<string, unknown>),
      { entityIds, businessUnitIds } as ArOutstandingQueryParams,
    );
    const response = await get<ApiResponse<TaxSummaryResultDto>>(url);
    return response.data.data ?? {
      fromDate: params.fromDate ?? '', toDate: params.toDate ?? '',
      documentType: params.documentType ?? 'ALL', jurisdictionCode: null,
      rows: [], componentCodes: [], jurisdictionCodes: [],
      totalQuantity: 0, totalTaxableValue: 0, totalTaxAmount: 0, componentTotals: {},
      totalDocumentCount: 0, totalLineCount: 0, backfilledLineCount: 0,
      baseCurrencyCode: null, mixedBaseCurrency: false,
    };
  }

  async exportTaxSummary(params: TaxSummaryQueryParams): Promise<void> {
    const { entityIds, businessUnitIds, ...rest } = params;
    const url = appendArScopeLists(
      buildEndpointWithQuery(reportEndpoints.taxSummaryExport(), rest as Record<string, unknown>),
      { entityIds, businessUnitIds } as ArOutstandingQueryParams,
    );
    const response = await get<Blob>(url, { responseType: 'blob' });
    downloadBlob(response, `TaxSummary_${params.fromDate ?? ''}_${params.toDate ?? ''}.xlsx`);
  }

  /**
   * Drill-down statement for one customer ledger on the AR Outstanding report.
   * Uses the report's own endpoint (not /reports/ledger) because that one requires
   * UI.LEDGER-STATEMENT.VIEW — which Managers don't have — and resolves the entity from
   * the request header, which breaks for a report consolidated across entities.
   */
  // --- Trial Balance (drill: ledger → voucher) ----------------------------
  // One entity (the header's). Business units follow the user's rights: none
  // picked = every BU they can access; picks outside their access → 403.

  async getTrialBalance(params: TrialBalanceQueryParams): Promise<TrialBalanceResultDto> {
    const response = await get<ApiResponse<TrialBalanceResultDto>>(trialBalanceUrl(reportEndpoints.trialBalance(), params));
    return response.data.data;
  }

  async getTrialBalanceLedger(ledgerId: ID, params: TrialBalanceQueryParams): Promise<LedgerStatementDto> {
    const response = await get<ApiResponse<LedgerStatementDto>>(trialBalanceUrl(reportEndpoints.trialBalanceLedger(), params, { ledgerId }));
    return response.data.data;
  }

  async getTrialBalanceVoucher(transactionId: string): Promise<VoucherViewDto> {
    const response = await get<ApiResponse<VoucherViewDto>>(reportEndpoints.trialBalanceVoucher(transactionId));
    return response.data.data;
  }

  async exportTrialBalance(params: TrialBalanceQueryParams): Promise<void> {
    const response = await get<Blob>(trialBalanceUrl(reportEndpoints.trialBalanceExport(), params), { responseType: 'blob' });
    downloadBlob(response, `TrialBalance_${params.fromDate}_to_${params.toDate}.xlsx`);
  }

  async getArOutstandingLedger(
    ledgerId: ID, fromDate: string, toDate: string, businessUnitIds?: string[],
  ): Promise<LedgerStatementDto> {
    let url = buildEndpointWithQuery(reportEndpoints.arOutstandingLedger(), { ledgerId, fromDate, toDate });
    (businessUnitIds ?? []).forEach((id) => {
      if (id) url += `${url.includes('?') ? '&' : '?'}businessUnitIds=${encodeURIComponent(id)}`;
    });
    const response = await get<ApiResponse<LedgerStatementDto>>(url);
    return response.data.data;
  }

  async exportArOutstanding(params: ArOutstandingQueryParams): Promise<void> {
    const { entityIds, businessUnitIds, ...rest } = params;
    const url = appendArScopeLists(
      buildEndpointWithQuery(reportEndpoints.arOutstandingExport(), rest as Record<string, unknown>),
      params,
    );
    const response = await get<Blob>(url, { responseType: 'blob' });
    const asOn = params.asOnDate ?? new Date().toISOString().slice(0, 10);
    downloadBlob(response, `ArOutstanding_${params.viewMode ?? 'GROUPED'}_${asOn}.xlsx`);
  }

  async exportDebtorsAgeing(
    params: DebtorsAgeingQueryParams,
    buckets?: AgeingBucket[],
  ): Promise<void> {
    const mode = params.viewMode ?? 'SUMMARY';
    const url = appendScopeLists(buildEndpointWithQuery(
      reportEndpoints.debtorsAgeingExport(),
      withBuckets({ ...params, viewMode: mode }, buckets),
    ), params);
    const response = await get<Blob>(url, { responseType: 'blob' });
    const asOf = params.asOfDate ?? new Date().toISOString().slice(0, 10);
    downloadBlob(response, `DebtorsAgeing_${mode}_${asOf}.xlsx`);
  }
}

/**
 * Serialise buckets into `bucketsJson` and DROP the scope arrays — those go on the URL as
 * repeated params (see appendScopeLists), because buildEndpointWithQuery would comma-join an
 * array and .NET's List&lt;Guid&gt; binder needs `?entityIds=a&entityIds=b`.
 */
function withBuckets(
  base: DebtorsAgeingQueryParams,
  buckets?: AgeingBucket[],
): Record<string, any> {
  const { entityIds, businessUnitIds, ...rest } = base;
  return buckets && buckets.length > 0 ? { ...rest, bucketsJson: JSON.stringify(buckets) } : { ...rest };
}

/** Append entityIds[] / businessUnitIds[] as repeated query params (List&lt;Guid&gt; binding). */
function appendScopeLists(url: string, params: DebtorsAgeingQueryParams): string {
  const parts: string[] = [];
  (params.entityIds ?? []).forEach((id) => { if (id) parts.push(`entityIds=${encodeURIComponent(id)}`); });
  (params.businessUnitIds ?? []).forEach((id) => { if (id) parts.push(`businessUnitIds=${encodeURIComponent(id)}`); });
  if (parts.length === 0) return url;
  return url + (url.includes('?') ? '&' : '?') + parts.join('&');
}

// Trial Balance URL: scalar params as query, then one businessUnitIds=… per BU.
function trialBalanceUrl(base: string, params: TrialBalanceQueryParams, extra: Record<string, unknown> = {}): string {
  const { businessUnitIds, ...rest } = params;
  let url = buildEndpointWithQuery(base, { ...rest, ...extra } as Record<string, unknown>);
  (businessUnitIds ?? []).forEach((id) => {
    if (id) url += `${url.includes('?') ? '&' : '?'}businessUnitIds=${encodeURIComponent(id)}`;
  });
  return url;
}

// Trigger a browser download from an axios blob response. Uses the Content-Disposition
// filename when the server provided one; otherwise falls back to the caller's suggestion.
// `headers` is typed loosely (any-valued record) because axios v1's AxiosResponseHeaders
// declares AxiosHeaderValue-typed values (string | string[] | number | boolean | null),
// which does not narrow to Record<string, string>. We only ever read two known keys via
// optional chaining, so the loose type is safe.
export function downloadBlob(
  response: { data: Blob; headers?: Record<string, unknown> },
  fallbackFilename: string,
) {
  const disposition = (response.headers?.['content-disposition']
    ?? response.headers?.['Content-Disposition']) as string | undefined;
  const match = disposition && /filename\*?=(?:UTF-8'')?"?([^;"]+)/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : fallbackFilename;
  const blobUrl = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

function emptyDayBook(page: number, pageSize: number): DayBookResultDto {
  return {
    items: [],
    page,
    pageSize,
    totalCount: 0,
    totalPages: 0,
    hasPrevious: false,
    hasNext: false,
    totalDebits: 0,
    totalCredits: 0,
  };
}

function emptyOpenBills(page: number, pageSize: number): OpenBillsResultDto {
  return {
    items: [],
    page,
    pageSize,
    totalCount: 0,
    totalPages: 0,
    hasPrevious: false,
    hasNext: false,
    totalOutstanding: 0,
  };
}

function emptyDebtorsAgeingSummary(page: number, pageSize: number): DebtorsAgeingSummaryResultDto {
  return {
    items: [],
    page,
    pageSize,
    totalCount: 0,
    totalPages: 0,
    hasPrevious: false,
    hasNext: false,
    asOfDate: new Date().toISOString().slice(0, 10),
    ageingBasis: 'DUE',
    currencyView: 'CONSOLIDATED',
    baseCurrencyCode: '',
    buckets: [],
    bucketTotals: [],
    grandTotal: 0,
    creditTotal: 0,
    shortPaidTotal: 0,
    netTotal: 0,
    currencySubtotals: [],
  };
}

function emptyDebtorsAgeingDetail(page: number, pageSize: number): DebtorsAgeingDetailResultDto {
  return {
    items: [],
    page,
    pageSize,
    totalCount: 0,
    totalPages: 0,
    hasPrevious: false,
    hasNext: false,
    asOfDate: new Date().toISOString().slice(0, 10),
    ageingBasis: 'DUE',
    currencyView: 'CONSOLIDATED',
    baseCurrencyCode: '',
    buckets: [],
    bucketTotals: [],
    grandTotal: 0,
    creditTotal: 0,
    shortPaidTotal: 0,
    netTotal: 0,
  };
}

export const reportService = new ReportService();
export default reportService;

/** Repeat entityIds/businessUnitIds as separate query params (arrays would be comma-joined). */
function appendArScopeLists(url: string, params: ArOutstandingQueryParams): string {
  const parts: string[] = [];
  (params.entityIds ?? []).forEach((id) => { if (id) parts.push(`entityIds=${encodeURIComponent(id)}`); });
  (params.businessUnitIds ?? []).forEach((id) => { if (id) parts.push(`businessUnitIds=${encodeURIComponent(id)}`); });
  if (parts.length === 0) return url;
  return url + (url.includes('?') ? '&' : '?') + parts.join('&');
}
